import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface FAQItem {
  question: string;
  answer: string;
}

function HomePage() {
  const [age, setAge] = useState<number>(0);
  const [weight, setWeight] = useState<number>(0);
  const [gender, setGender] = useState<string>('');
  const [result, setResult] = useState<string>('');
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState<string>('');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const calculatorSection = document.getElementById('calculator-section');
      if (calculatorSection) {
        const rect = calculatorSection.getBoundingClientRect();
        setShowScrollButton(rect.top > window.innerHeight);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToCalculator = () => {
    const calculatorSection = document.getElementById('calculator-section');
    if (calculatorSection) {
      calculatorSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const faqData: FAQItem[] = [
    {
      question: "ทำไมลูกถึงดูหิวบ่อย ทั้งที่ให้นมตามปริมาณที่แนะนำแล้ว?",
      answer: "ความหิวของทารกขึ้นอยู่กับหลายปัจจัย เช่น การเจริญเติบโต การเผาผลาญ และกิจกรรมในแต่ละวัน ปริมาณที่แนะนำเป็นเพียงค่าเฉลี่ย คุณสามารถปรับเพิ่มได้ตามความต้องการของลูก แต่ควรสังเกตอาการและปรึกษาแพทย์"
    },
    {
      question: "ควรให้นมตามตารางเวลา หรือให้ตามความต้องการของลูก?",
      answer: "ทั้งสองวิธีมีข้อดี การให้ตามตารางช่วยสร้างระเบียบ แต่การให้ตามความต้องการจะตอบสนองความหิวของลูกได้ดีกว่า ขึ้นอยู่กับความเหมาะสมของแต่ละครอบครัว"
    },
    {
      question: "ทำไมลูกถึงแหวะนมบ่อย?",
      answer: "อาจเกิดจากการให้นมมากเกินไปในแต่ละมื้อ ลองแบ่งมื้อให้ถี่ขึ้นแต่ปริมาณต่อมื้อน้อยลง และจัดท่าให้นมที่เหมาะสมเพื่อลดการสำลัก"
    }
  ];

  const calculateMilkIntake = () => {
    // ตรวจสอบค่าที่กรอก
    if (age === 0) {
      setError('กรุณากรอกอายุที่มากกว่า 0');
      return;
    }
    if (age > 24) {
      setError('อายุต้องไม่เกิน 24 เดือน');
      return;
    }
    if (weight === 0) {
      setError('กรุณากรอกน้ำหนักที่มากกว่า 0');
      return;
    }
    if (!gender) {
      setError('กรุณาเลือกเพศ');
      return;
    }

    setError('');
    setIsCalculating(true);
    
    // สูตรคำนวณปริมาณนมโดยประมาณ
    // เด็กอายุ 0-6 เดือน: 150-200 มล. ต่อ กก. ต่อวัน
    // เด็กอายุ > 6 เดือน: 120-150 มล. ต่อ กก. ต่อวัน
    
    let dailyIntake: number;
    let feedingsPerDay: number;
    
    if (age <= 6) {
      dailyIntake = weight * 150; // ใช้ค่าเฉลี่ย 150 มล./กก./วัน
      feedingsPerDay = 8; // โดยประมาณสำหรับทารกแรกเกิด - 6 เดือน
    } else {
      dailyIntake = weight * 120; // ใช้ค่าเฉลี่ย 120 มล./กก./วัน
      feedingsPerDay = 6; // โดยประมาณสำหรับทารก 6 เดือนขึ้นไป
    }

    const perFeeding = Math.round(dailyIntake / feedingsPerDay);
    
    // แปลงมิลลิลิตรเป็นออนซ์ (1 ออนซ์ = 29.5735 มล.)
    const dailyIntakeOz = Math.round(dailyIntake / 29.5735 * 10) / 10;
    const perFeedingOz = Math.round(perFeeding / 29.5735 * 10) / 10;

    setTimeout(() => {
      setResult(`
        ปริมาณนมที่แนะนำต่อวัน: 
        ${dailyIntake} มล. (${dailyIntakeOz} oz.)
        
        จำนวนมื้อต่อวัน: ${feedingsPerDay} มื้อ
        
        ปริมาณนมต่อมื้อ: 
        ${perFeeding} มล. (${perFeedingOz} oz.)
      `);
      setIsCalculating(false);
    }, 500);
  };

  const handleAgeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.floor(Number(e.target.value.replace(/^0+/, '')));
    if (value > 24) {
      setError('อายุต้องไม่เกิน 24 เดือน');
    } else if (value === 0) {
      setError('กรุณากรอกอายุที่มากกว่า 0');
    } else {
      setError('');
    }
    setAge(value);
  };

  const handleWeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value.replace(/^0+/, ''));
    setWeight(value);
    if (value === 0) {
      setError('กรุณากรอกน้ำหนักที่มากกว่า 0');
    } else {
      setError('');
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f6f7f7' }}>
      <div className="w-full px-4 sm:px-8 lg:p-16">
        <div className="max-w-[1200px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-20 items-start">
            {/* ส่วนข้อมูลด้านซ้าย */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-7 p-6 sm:p-8"
            >
              <h2 className="text-[32px] font-bold text-[#454c60] mb-8 font-['IBM_Plex_Sans_Thai_Looped']" style={{ fontSize: '32px' }}>
                ทำไม? ต้องคำนวณปริมาณนมที่เหมาะสมกับลูกแต่ละช่วงวัย
              </h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  การให้นมในปริมาณที่เหมาะสมเป็นสิ่งสำคัญอย่างยิ่งต่อการเจริญเติบโตของทารก เนื่องจาก:
                </p>
                <ul className="list-disc pl-5 space-y-2">
                  <li>
                    <span className="font-semibold">การเจริญเติบโตที่เหมาะสม:</span> ทารกต้องการสารอาหารที่เพียงพอต่อการพัฒนาร่างกายและสมอง
                  </li>
                  <li>
                    <span className="font-semibold">ป้องกันภาวะทุพโภชนาการ:</span> การให้นมน้อยเกินไปอาจทำให้ทารกได้รับสารอาหารไม่เพียงพอ ในขณะที่การให้นมมากเกินไปอาจนำไปสู่ภาวะน้ำหนักเกิน
                  </li>
                  <li>
                    <span className="font-semibold">พัฒนาการที่ดี:</span> ปริมาณนมที่เหมาะสมช่วยให้ทารกมีพลังงานเพียงพอสำหรับการเรียนรู้และพัฒนาการด้านต่างๆ
                  </li>
                  <li>
                    <span className="font-semibold">การย่อยที่เหมาะสม:</span> ปริมาณนมที่พอดีช่วยให้ระบบย่อยอาหารของทารกทำงานได้อย่างมีประสิทธิภาพ
                  </li>
                </ul>
                <p className="mt-4 mb-16">
                  การคำนวณนี้ช่วยประมาณปริมาณนมที่เหมาะสมตามอายุและน้ำหนักของทารก โดยอ้างอิงจากค่าเฉลี่ยมาตรฐาน อย่างไรก็ตาม ควรปรึกษาแพทย์หรือผู้เชี่ยวชาญเพื่อรับคำแนะนำที่เหมาะสมกับทารกของคุณ
                </p>

                <div className="pt-16">
                  <h3 className="text-xl font-bold text-blue-600 mb-6 font-['IBM_Plex_Sans_Thai_Looped']">
                    คำถามที่เหล่าแม่ๆ สงสัยเกี่ยวกับการให้นม
                  </h3>
                  <div className="space-y-4">
                    {faqData.map((faq, index) => (
                      <div key={index} className="rounded-lg overflow-hidden bg-opacity-50 bg-white">
                        <button
                          onClick={() => setOpenFaq(openFaq === index ? null : index)}
                          className="w-full px-4 py-3 text-left flex justify-between items-center hover:bg-opacity-70 hover:bg-white transition-colors"
                        >
                          <span className="font-semibold text-blue-700 flex-1 mr-4">{faq.question}</span>
                          <div className="w-5 h-5 flex-shrink-0">
                            <svg
                              className={`w-full h-full transform transition-transform duration-200 ${
                                openFaq === index ? 'rotate-180' : ''
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </div>
                        </button>
                        {openFaq === index && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="px-4 pb-3"
                          >
                            <p className="text-gray-600">{faq.answer}</p>
                          </motion.div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* ส่วนคำนวณด้านขวา */}
            <motion.div 
              id="calculator-section"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-5"
            >
              <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
                <div className="text-center mb-8">
                  <h1 className="text-[32px] font-bold text-blue-600 mb-2 font-['IBM_Plex_Sans_Thai_Looped']" style={{ fontSize: '32px' }}>
                    คำนวณปริมาณนม
                  </h1>
                  <p className="text-gray-600">สำหรับทารกแรกเกิด - 2 ปี</p>
                </div>

                <div className="space-y-6">
                  <motion.div 
                    whileHover={{ scale: 1.02 }}
                    className="rounded-xl p-4 bg-purple-light"
                  >
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      เพศ
                    </label>
                    <div className="flex space-x-4">
                        <button
                            onClick={() => {
                            setGender('male');
                            setError('');
                            }}
                            className={`flex-1 p-4 rounded-lg transition-all ${
                            gender === 'male'
                                ? 'bg-blue-500 text-white'
                                : 'bg-white bg-opacity-50 text-gray-700'
                            }`}
                        >
                            ชาย
                        </button>
                        <button
                            onClick={() => {
                            setGender('female');
                            setError('');
                            }}
                            className={`flex-1 p-4 rounded-lg transition-all ${
                            gender === 'female'
                                ? 'bg-pink-500 text-white'
                                : 'bg-white bg-opacity-50 text-gray-700'
                            }`}
                        >
                            หญิง
                        </button>
                    </div>

                  </motion.div>

                  <motion.div 
                    whileHover={{ scale: 1.02 }}
                    className="rounded-xl p-4 bg-blue-light"
                  >
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      อายุ (เดือน)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="24"
                      step="1"
                      value={age || ''}
                      onChange={handleAgeChange}
                      onFocus={(e) => {
                        if (age === 0) {
                          e.target.value = '';
                        }
                      }}
                      onBlur={(e) => {
                        if (e.target.value === '') {
                          setAge(0);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === '.' || e.key === '-' || e.key === 'e') {
                          e.preventDefault();
                        }
                      }}
                      className="w-full px-4 py-3 border-2 border-blue-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="กรุณาระบุอายุ (1-24)"
                    />
                  </motion.div>
                  
                  <motion.div 
                    whileHover={{ scale: 1.02 }}
                    className="rounded-xl p-4 bg-pink-light"
                  >
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      น้ำหนัก (กิโลกรัม)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0.1"
                      value={weight || ''}
                      onChange={handleWeightChange}
                      onFocus={(e) => {
                        if (weight === 0) {
                          e.target.value = '';
                        }
                      }}
                      onBlur={(e) => {
                        if (e.target.value === '') {
                          setWeight(0);
                        }
                      }}
                      className="w-full px-4 py-3 border-2 border-pink-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all"
                      placeholder="กรุณาระบุน้ำหนัก"
                    />
                  </motion.div>

                  {error && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="p-3 rounded-lg bg-red-50 border-2 border-red-200 text-red-600 text-sm font-medium text-center"
                    >
                      {error}
                    </motion.div>
                  )}

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={calculateMilkIntake}
                    disabled={!gender || age === 0 || age > 24 || weight === 0}
                    className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 px-4 rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all font-medium text-lg shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isCalculating ? 'กำลังคำนวณ...' : 'คำนวณ'}
                  </motion.button>

                  {result && (
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-6 bg-gradient-to-r from-blue-50 to-pink-50 rounded-xl p-6 shadow-inner"
                    >
                      <h2 className="text-lg font-semibold text-blue-700 mb-3">ผลการคำนวณ</h2>
                      <pre className="whitespace-pre-line text-gray-700 font-medium">
                        {result}
                      </pre>
                    </motion.div>
                  )}

                  <div className="text-center text-sm text-gray-500 mt-8">
                    <p>* คำนวณโดยใช้ค่าเฉลี่ยมาตรฐาน</p>
                    <p>* ปรึกษาแพทย์หรือผู้เชี่ยวชาญสำหรับคำแนะนำที่เหมาะสมกับทารกของคุณ</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Scroll to Calculator Button (Mobile Only) */}
      {showScrollButton && (
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          onClick={scrollToCalculator}
          className="fixed bottom-6 right-6 bg-blue-500 text-white px-6 py-3 rounded-full shadow-lg lg:hidden z-50 hover:bg-blue-600 transition-colors flex items-center gap-2"
        >
          <span>คำนวณปริมาณนม</span>
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>
        </motion.button>
      )}
    </div>
  );
}

export default HomePage;
